import {EventBus} from "../../../common/ts/events/EventBus";
import {PageLoadedEvent, WhitelabelEvent} from "../../../common/ts/events/Events";
import {connect} from "../../../common/ts/connect";
import dom from "../../../common/ts/core/dom";
import {CssBreakpoints} from "./CssBreakpoints";
import {setInteractionFlag} from "./SerpInteraction";


const eventBus: EventBus = EventBus.getInstance();
eventBus.subscribeWithId(
    PageLoadedEvent.TYPE,
    {
        id: 'nestoria-serp-filters-loaded-subscriber',
        callback: onSerpFiltersLoad
    }
);

function onSerpFiltersLoad(event: WhitelabelEvent): void {
    connect(() => {
        dom.init()
        new SerpFilters().init()
    })
}

class SerpFilters {
    private readonly serpFiltersDom: SerpFiltersDom | null;

    constructor() {
        this.serpFiltersDom = new SerpFiltersDom();
        if (!this.serpFiltersDom.filters) {
            return;
        }
    }

    init() {
        if (!this.serpFiltersDom) {
            return;
        }
        this.serpFiltersDom.prefillForm()
        if(CssBreakpoints.get().isDesktop()) {
            this.initForDesktop();
        } else {
            this.initForMobile();
        }
    }

    private initForMobile(): void {
        this.serpFiltersDom?.toggleButton?.addEventListener('click', () => {
            this.serpFiltersDom?.toggleFiltersForMobile();
        });
        this.serpFiltersDom?.closeButton?.addEventListener('click', () => {
            this.serpFiltersDom?.toggleFiltersForMobile();
        });
        this.serpFiltersDom?.resetButtons?.forEach(button => button.addEventListener('click', () => {
            this.serpFiltersDom?.resetForm();
        }));
        this.serpFiltersDom?.linkButtons?.forEach(link => link.addEventListener('click', (event) => {
            event.preventDefault();
            const href = (link as HTMLAnchorElement).href;
            this.serpFiltersDom?.replaceFormAction(href);
            this.serpFiltersDom?.markLinkAsActive(link);
        }));
        this.serpFiltersDom?.registerFormSubmitHandler();
    }

    private initForDesktop(): void {
        this.serpFiltersDom?.formFields?.forEach(field => {
            field.addEventListener('change', () => {
                this.serpFiltersDom?.submitForm('filtering');
            });
        });
        this.serpFiltersDom?.resetButtons?.forEach(button => button.addEventListener('click', () => {
            this.serpFiltersDom?.resetForm();
            this.serpFiltersDom?.submitForm('filtering');
        }));
        this.serpFiltersDom?.linkButtons?.forEach(link => link.addEventListener('click', (event) => {
            event.preventDefault();
            const href = (event.currentTarget as HTMLAnchorElement).href;
            this.serpFiltersDom?.replaceFormAction(href);
            this.serpFiltersDom?.markLinkAsActive(link);
            this.serpFiltersDom?.submitForm('ordering');
        }));
    }
}

class SerpFiltersDom {
    private readonly form: HTMLFormElement | null = null;
    public formFields: NodeListOf<HTMLInputElement | HTMLSelectElement> | null = null;
    public filters: HTMLElement | null;
    public toggleButton: HTMLButtonElement | null = null;
    public closeButton: HTMLButtonElement | null = null;
    public resetButtons: Element[] = [];
    public linkButtons: NodeListOf<HTMLAnchorElement> | null = null;
    private readonly toolbar: HTMLElement | null = null;
    private readonly footer: HTMLElement | null = null;

    constructor(
        filtersSelector: string = '.js-serp-filters',
        showFilterButtonSelector: string = '.js-filters-toggle',
        closeButtonSelector: string = '.js-filters-close',
        resetButtonSelector: string = '.js-filters-reset',
        toolbarSelector: string = '.js-toolbar-container',
        footerSelector: string = 'footer',
    ) {
        this.filters = dom.select(filtersSelector);
        if (!this.filters) {
            return;
        }
        this.toggleButton = dom.select(showFilterButtonSelector);
        this.closeButton = dom.select(closeButtonSelector);
        this.resetButtons = dom.selectAll(resetButtonSelector);
        this.footer = dom.select(footerSelector);
        this.toolbar = dom.select(toolbarSelector);
        this.form = this.filters.querySelector('form');
        if (this.form) {
            this.formFields = this.form.querySelectorAll('input, select');
            this.linkButtons = this.form.querySelectorAll('.js-operation-type a, .js-property-type a');
        }
    }

    public markLinkAsActive(link: HTMLAnchorElement) {
        if (!this.linkButtons) {
            return;
        }
        link.parentElement?.querySelectorAll('a').forEach(siblinkLink => {
            siblinkLink.classList.remove('active')
        });
        link.classList.add('active');
    }

    public replaceFormAction(newAction: string) {
        if (this.form) {
            this.form.action = newAction;
        }
    }

    public prefillForm(): void {
        if (!this.formFields) {
            return;
        }
        const queryParameters = new URLSearchParams(window.location.search);
        const uniqueKeys = Array.from(new Set(Array.from(queryParameters.keys())));
        uniqueKeys.forEach(( key) => {
            const rawValues = queryParameters.getAll(key);
            const queryValues = rawValues
                .flatMap(value => value.split(','))
                .map(value => value.trim())
                .filter(value => value.length > 0);
            // @ts-ignore
            const matchingFields = Array.from(this.formFields).filter(
                field => field.name === key
            );
            matchingFields.forEach((field) => {
                this.applyQueryValuesToField(queryValues, field);
            });
        });
    }

    public submitForm(interactionType: string = 'filtering') {
        if (!this.form) {
            return;
        }
        const formData = new FormData(this.form);
        const params = new URLSearchParams();
        const valuesByName = new Map<string, string[]>();
        formData.forEach((value, key) => {
            if (!valuesByName.has(key)) {
                valuesByName.set(key, []);
            }
            valuesByName.get(key)!.push(value.toString());
        });
        valuesByName.forEach((values, key) => {
            if (values.length > 1) {
                params.set(key, values.join(','));
            } else {
                params.set(key, values[0]);
            }
        });
        const action = this.form.action || window.location.pathname;

        setInteractionFlag(interactionType);
        window.location.href = `${action}?${params.toString()}`;
    }

    public resetForm() {
        this.form?.reset();
    }

    public registerFormSubmitHandler(): void {
        this.form?.addEventListener('submit', () => {
            setInteractionFlag('filtering');
        });
    }

    public toggleFiltersForMobile() {
        this.scrollToTop()
        this.toggleFilters()
        this.toggleSiblingsElements()
        this.toggleElement(this.toolbar)
        this.toggleElement(this.footer)
        this.toggleShowFilterButton()
    }

    private toggleFilters(): void {
        this.filters?.classList.toggle('serp-filters--visible');
    }

    private toggleSiblingsElements(): void {
        if (!this.filters) {
            return;
        }
        const siblings = this.getFiltersSiblings(this.filters);
        siblings.forEach((sibling: HTMLElement) => {
            this.toggleElement(sibling)
        });
    }

    private getFiltersSiblings(element: HTMLElement): HTMLElement[] {
        const siblings: HTMLElement[] = [];
        let sibling = element.parentNode?.firstChild;
        while (sibling) {
            if (sibling.nodeType === 1 && sibling !== element) {
                siblings.push(sibling as HTMLElement);
            }
            sibling = sibling.nextSibling;
        }
        return siblings;
    }

    private toggleElement(element: HTMLElement | null): void {
        if (!element) {
            return;
        }
        if (element.style.display === 'none') {
            element.style.removeProperty('display');
        } else {
            element.style.display = 'none';
        }
    }

    private toggleShowFilterButton() {
        this.toggleButton?.classList.toggle('filters-toggle--hidden');
    }

    private applyQueryValuesToField(queryValues: string[], field: Element): void {
        if (field instanceof HTMLInputElement) {
            this.applyValuesToInput(queryValues, field);
        } else if (field instanceof HTMLSelectElement) {
            this.applyValuesToSelect(queryValues, field);
        }
    }

    private applyValuesToInput(queryValues: string[], input: HTMLInputElement): void {
        const firstValue = queryValues[0] ?? "";
        if (input.type === "checkbox" || input.type === "radio") {
            input.checked = queryValues.includes(input.value);
            return;
        }
        input.value = firstValue;
    }

    private applyValuesToSelect(queryValues: string[], select: HTMLSelectElement): void {
        const firstValue = queryValues[0] ?? "";
        if (select.multiple) {
            Array.from(select.options).forEach((option) => {
                option.selected = queryValues.includes(option.value);
            });
            return;
        }
        select.value = firstValue;
    }

    private scrollToTop() {
        window.scrollTo({
            top: 0,
        });
    }
}